import { useState } from 'react';
import {
  Printer,
  Building2,
  FileText,
  CreditCard,
  Hash,
  Calendar,
  Users,
} from 'lucide-react';
import dayjs from 'dayjs';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import DatePopoverSingle from '@/components/DatePopoverSingle';

import type { IInvoiceDetails } from '@/pages/PurchasesContainer/IncomingInvoice/api/IncomingInvoice.interface';
import { PrintIncomeInvoice } from '@/utils/AppPrinter/IncomeInvoices/useIncomePrint';

interface SupplierPrintData {
  supplierName: string;
  commercialRegistration: string;
  taxCard: string;
  invoiceNumber: string;
  invoiceDate: string | null;
  clientCode: string;
}

interface Props {
  invoice: IInvoiceDetails;
  children: React.ReactNode;
}

export function IncomePrintDialog({ invoice, children }: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [supplierData, setSupplierData] = useState<SupplierPrintData>({
    supplierName: invoice?.header?.SupplierName || '',
    commercialRegistration: '',
    taxCard: '',
    invoiceNumber: invoice?.header?.InvoiceID || '',
    invoiceDate: invoice?.header?.TrDate
      ? dayjs(invoice.header.TrDate).format('YYYY-MM-DD')
      : null,
    clientCode: '',
  });

  const handleInputChange = (
    field: keyof SupplierPrintData,
    value: string | null,
  ) => {
    setSupplierData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handlePrint = async () => {
    // Validate required fields
    if (!supplierData.supplierName.trim()) {
      toast.error('خطأ في البيانات', {
        description: 'يجب إدخال اسم المورد',
      });
      return;
    }

    if (!supplierData.commercialRegistration.trim()) {
      toast.error('خطأ في البيانات', {
        description: 'يجب إدخال رقم السجل التجاري',
      });
      return;
    }

    if (!supplierData.taxCard.trim()) {
      toast.error('خطأ في البيانات', {
        description: 'يجب إدخال رقم البطاقة الضريبية',
      });
      return;
    }

    if (!supplierData.invoiceNumber.trim()) {
      toast.error('خطأ في البيانات', {
        description: 'يجب إدخال رقم الفاتورة',
      });
      return;
    }

    if (!supplierData.invoiceDate || !supplierData.invoiceDate.trim()) {
      toast.error('خطأ في البيانات', {
        description: 'يجب إدخال تاريخ الفاتورة',
      });
      return;
    }

    if (!supplierData.clientCode.trim()) {
      toast.error('خطأ في البيانات', {
        description: 'يجب إدخال كود العميل',
      });
      return;
    }

    try {
      setIsLoading(true);

      // Convert date to DD/MM/YYYY format for PDF generation
      const printData = {
        ...supplierData,
        invoiceDate: supplierData.invoiceDate
          ? dayjs(supplierData.invoiceDate).format('DD/MM/YYYY')
          : '',
      };

      await PrintIncomeInvoice(invoice, false, printData);

      toast.success('تم الطباعة بنجاح', {
        description: 'تم إنشاء الفاتورة وفتحها في نافذة جديدة',
      });

      setIsOpen(false);
    } catch (error) {
      console.error('Print error:', error);
      toast.error('خطأ في الطباعة', {
        description: 'حدث خطأ أثناء طباعة الفاتورة',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    // Reset form to initial values
    setSupplierData({
      supplierName: invoice?.header?.SupplierName || '',
      commercialRegistration: '',
      taxCard: '',
      invoiceNumber: invoice?.header?.InvoiceID || '',
      invoiceDate: invoice?.header?.TrDate
        ? dayjs(invoice.header.TrDate).format('YYYY-MM-DD')
        : null,
      clientCode: '',
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Printer className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            طباعة فاتورة الوارد
          </DialogTitle>
          <DialogDescription>
            يرجى إدخال تفاصيل المورد المطلوبة لطباعة الفاتورة. جميع الحقول
            مطلوبة.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Grid Layout for Form Fields */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Supplier Name - spans full width */}
            <div className="col-span-full space-y-2">
              <Label
                htmlFor="supplier-name"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Building2 className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                اسم المورد
              </Label>
              <Input
                id="supplier-name"
                placeholder="أدخل اسم المورد..."
                value={supplierData.supplierName}
                onChange={(e) =>
                  handleInputChange('supplierName', e.target.value)
                }
                className="text-right"
                dir="rtl"
              />
            </div>

            {/* Commercial Registration */}
            <div className="space-y-2">
              <Label
                htmlFor="commercial-reg"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <FileText className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                رقم السجل التجاري
              </Label>
              <Input
                id="commercial-reg"
                placeholder="أدخل رقم السجل التجاري..."
                value={supplierData.commercialRegistration}
                onChange={(e) =>
                  handleInputChange('commercialRegistration', e.target.value)
                }
                className="text-right"
                dir="rtl"
              />
            </div>

            {/* Tax Card */}
            <div className="space-y-2">
              <Label
                htmlFor="tax-card"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <CreditCard className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                رقم البطاقة الضريبية
              </Label>
              <Input
                id="tax-card"
                placeholder="أدخل رقم البطاقة الضريبية..."
                value={supplierData.taxCard}
                onChange={(e) => handleInputChange('taxCard', e.target.value)}
                className="text-right"
                dir="rtl"
              />
            </div>

            {/* Invoice Number */}
            <div className="space-y-2">
              <Label
                htmlFor="invoice-number"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Hash className="h-4 w-4 text-orange-600 dark:text-orange-400" />
                رقم الفاتورة
              </Label>
              <Input
                id="invoice-number"
                placeholder="أدخل رقم الفاتورة..."
                value={supplierData.invoiceNumber}
                onChange={(e) =>
                  handleInputChange('invoiceNumber', e.target.value)
                }
                className="text-right"
                dir="rtl"
              />
            </div>

            {/* Invoice Date */}
            <div className="space-y-2">
              <Label
                htmlFor="invoice-date"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Calendar className="h-4 w-4 text-red-600 dark:text-red-400" />
                تاريخ الفاتورة
              </Label>
              <DatePopoverSingle
                value={supplierData.invoiceDate}
                selectFn={(date) => handleInputChange('invoiceDate', date)}
                placeholder="اختر تاريخ الفاتورة"
                width="w-full"
              />
            </div>

            {/* Client Code */}
            <div className="space-y-2">
              <Label
                htmlFor="client-code"
                className="flex items-center gap-2 text-sm font-medium"
              >
                <Users className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                كود العميل
              </Label>
              <Input
                id="client-code"
                placeholder="أدخل كود العميل..."
                value={supplierData.clientCode}
                onChange={(e) =>
                  handleInputChange('clientCode', e.target.value)
                }
                className="text-right"
                dir="rtl"
              />
            </div>
          </div>

          {/* Current Invoice Info */}
          <div className="rounded-lg bg-slate-50 p-3 dark:bg-slate-800/50">
            <h4 className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-300">
              معلومات إضافية:
            </h4>
            <div className="text-sm text-slate-600 dark:text-slate-400">
              <p>عدد الأصناف: {invoice?.transactions?.length || 0}</p>
            </div>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={handleCancel} disabled={isLoading}>
            إلغاء
          </Button>
          <Button
            onClick={handlePrint}
            disabled={isLoading}
            className="bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600"
          >
            {isLoading ? (
              <>
                <div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                جاري الطباعة...
              </>
            ) : (
              <>
                <Printer className="mr-2 h-4 w-4" />
                طباعة الفاتورة
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
